import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { Menu, X, LogOut, Shield, Gamepad2, ChevronDown } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

function Icon({ name, ...props }) {
    const C = Icons[name] || Icons.Circle;
    return <C {...props} />;
}

export default function Shell({ mode, nav, basePath, bottomNav, topbarExtra }) {
    const { user, logout, isAdmin } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);

    const activeId = location.pathname.split("/")[2] || nav[0].id;
    const isAdminMode = mode === "admin";
    const accent = isAdminMode ? "#D9480F" : "#FF6B00";

    const NavLinks = ({ onClick }) => (
        <nav className="flex-1 space-y-0.5 overflow-y-auto px-2 py-3">
            {nav.map((item) => {
                const active = item.id === activeId;
                return (
                    <Link
                        key={item.id}
                        to={`${basePath}/${item.id}`}
                        onClick={onClick}
                        data-testid={`nav-${item.id}`}
                        className={`group flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors ${
                            active
                                ? "bg-hazard/12 text-hazard font-semibold"
                                : "text-muted-foreground hover:bg-accent hover:text-foreground"
                        }`}
                        style={active ? { boxShadow: `inset 2px 0 0 ${accent}` } : {}}
                    >
                        <Icon name={item.icon} size={17} />
                        <span className="font-heading uppercase tracking-wide">{item.label}</span>
                    </Link>
                );
            })}
        </nav>
    );

    const Brand = () => (
        <div className="flex items-center gap-2.5 px-4 py-4 border-b border-border">
            <div className="flex h-9 w-9 items-center justify-center rounded-md hazard-stripes border border-hazard/40">
                <Shield size={18} className="text-hazard" />
            </div>
            <div className="leading-tight">
                <div className="font-heading text-lg font-extrabold tracking-wider text-foreground">OUTPOST</div>
                <div className="label-mono text-hazard -mt-1">NETWORK</div>
            </div>
        </div>
    );

    return (
        <div className="min-h-screen hud-grid flex">
            {/* Desktop sidebar */}
            <aside className="hidden lg:flex w-60 flex-col border-r border-border bg-card/70 backdrop-blur fixed inset-y-0 left-0 z-30">
                <Brand />
                <NavLinks />
                <div className="border-t border-border p-3">
                    <ModeToggle isAdminMode={isAdminMode} isAdmin={isAdmin} navigate={navigate} />
                </div>
            </aside>

            {/* Mobile drawer */}
            {open && (
                <div className="lg:hidden fixed inset-0 z-50 flex">
                    <div className="absolute inset-0 bg-black/70" onClick={() => setOpen(false)} />
                    <aside className="relative w-64 flex flex-col bg-card border-r border-border">
                        <button className="absolute right-3 top-4 text-muted-foreground" onClick={() => setOpen(false)}>
                            <X size={20} />
                        </button>
                        <Brand />
                        <NavLinks onClick={() => setOpen(false)} />
                        <div className="border-t border-border p-3">
                            <ModeToggle isAdminMode={isAdminMode} isAdmin={isAdmin} navigate={navigate} />
                        </div>
                    </aside>
                </div>
            )}

            {/* Main */}
            <div className="flex-1 lg:ml-60 flex flex-col min-w-0">
                <header className="sticky top-0 z-20 flex items-center gap-3 border-b border-border bg-card/80 backdrop-blur px-4 py-3">
                    <button className="lg:hidden text-foreground" onClick={() => setOpen(true)} data-testid="mobile-menu-btn">
                        <Menu size={22} />
                    </button>
                    <div className="flex items-center gap-2">
                        <span className="label-mono text-muted-foreground hidden sm:inline">
                            {isAdminMode ? "ADMIN CONTROL CENTER" : "PLAYER DASHBOARD"}
                        </span>
                    </div>
                    <div className="flex-1">{topbarExtra}</div>
                    <div className="flex items-center gap-3">
                        <Link to="/player/notifications" data-testid="topbar-notifications" className="relative text-muted-foreground hover:text-foreground">
                            <Icons.Bell size={19} />
                        </Link>
                        <div className="flex items-center gap-2">
                            <img
                                src={user?.avatar || `https://api.dicebear.com/7.x/bottts/svg?seed=${user?.username || "op"}`}
                                alt="avatar" className="h-8 w-8 rounded-md border border-border bg-background"
                            />
                            <div className="hidden sm:block leading-tight">
                                <div className="text-sm font-semibold text-foreground">{user?.username}</div>
                                <div className="label-mono text-muted-foreground">{(user?.role || "").replace(/_/g, " ")}</div>
                            </div>
                        </div>
                        <button onClick={logout} data-testid="logout-btn" className="text-muted-foreground hover:text-danger" title="Log out">
                            <LogOut size={18} />
                        </button>
                    </div>
                </header>

                <main className="flex-1 p-4 sm:p-6 pb-24 lg:pb-6 max-w-[1500px] w-full mx-auto">
                    <OutletRenderer />
                </main>
            </div>

            {/* Mobile bottom nav */}
            {bottomNav && (
                <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 border-t border-border bg-card/95 backdrop-blur grid grid-cols-5">
                    {bottomNav.map((item) => {
                        const active = item.id === activeId;
                        return (
                            <Link key={item.id} to={`${basePath}/${item.id}`} data-testid={`bottomnav-${item.id}`}
                                className={`flex flex-col items-center gap-0.5 py-2.5 ${active ? "text-hazard" : "text-muted-foreground"}`}>
                                <Icon name={item.icon} size={19} />
                                <span className="label-mono" style={{ fontSize: "0.6rem" }}>{item.label}</span>
                            </Link>
                        );
                    })}
                </nav>
            )}
        </div>
    );
}

function ModeToggle({ isAdminMode, isAdmin, navigate }) {
    if (!isAdmin) {
        return (
            <div className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-xs text-muted-foreground">
                <Gamepad2 size={14} className="text-hazard" /> Player access
            </div>
        );
    }
    return (
        <div className="grid grid-cols-2 gap-1 rounded-md border border-border p-1">
            <button data-testid="mode-switch-player"
                onClick={() => navigate("/player/dashboard")}
                className={`flex items-center justify-center gap-1 rounded-sm py-1.5 label-mono transition-colors ${!isAdminMode ? "bg-hazard text-black" : "text-muted-foreground hover:text-foreground"}`}>
                <Gamepad2 size={13} /> Player
            </button>
            <button data-testid="mode-switch-admin"
                onClick={() => navigate("/admin/overview")}
                className={`flex items-center justify-center gap-1 rounded-sm py-1.5 label-mono transition-colors ${isAdminMode ? "bg-burnt text-white" : "text-muted-foreground hover:text-foreground"}`}>
                <Shield size={13} /> Admin
            </button>
        </div>
    );
}

// Renders the matched child route
import { Outlet } from "react-router-dom";
function OutletRenderer() {
    return <Outlet />;
}
